import { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import {
  addCalendarDays,
  diffCalendarDays,
  type Event,
} from "@event-planner/shared";

interface DuplicateEventDialogProps {
  open: boolean;
  /** Source event being duplicated. Null while closed. */
  event: Event | null;
  onClose: () => void;
  /** Called with the copy's name + new start date (YYYY-MM-DD) on confirm. */
  onConfirm: (name: string, newStartDate: string) => void;
  loading?: boolean;
}

const DATE_FORMAT = "YYYY-MM-DD";

/**
 * Collects the copy's name and new start date for the `duplicateEvent`
 * callable. The whole schedule shifts with the start date (each
 * session keeps its local time-of-day); attendees are never copied.
 *
 * The date field mirrors SchemaField's DatePickerField conventions:
 * MUI X DatePicker, click-anywhere-to-open, pointer cursor, and a
 * `date-picker-*` testid wrapper so the shared E2E `fillDatePicker`
 * helper drives it like every other date field in the app.
 */
export function DuplicateEventDialog({
  open,
  event,
  onClose,
  onConfirm,
  loading = false,
}: DuplicateEventDialogProps) {
  const [name, setName] = useState("");
  /** YYYY-MM-DD, or null while the picker holds an invalid/empty value. */
  const [startDate, setStartDate] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Re-seed the fields from the source event each time the dialog
  // opens (mirrors TypeToConfirmDialog's reset-on-open effect).
  useEffect(() => {
    if (open && event) {
      setName(`${event.name} (copy)`);
      setStartDate(event.startDate);
    }
  }, [open, event]);

  const canConfirm = !loading && name.trim().length > 0 && !!startDate;

  // Display-only preview of the computed end date (the callable owns
  // the authoritative math — same shared helpers on both sides).
  const endDatePreview =
    event && startDate
      ? addCalendarDays(startDate, diffCalendarDays(event.startDate, event.endDate))
      : null;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Duplicate event</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Copies the event's details, branding, categories, FAQs,
          locations, sessions, and groups. Attendees, invitations, and
          group members are not copied. Every session shifts with the new
          start date, keeping its time of day.
        </DialogContentText>
        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          disabled={loading}
          sx={{ mb: 2, mt: 1 }}
          inputProps={{ "aria-label": "Name for the duplicated event" }}
        />
        <div data-testid="date-picker-duplicateStartDate">
          <DatePicker
            label="Start date"
            format={DATE_FORMAT}
            value={startDate ? dayjs(startDate) : null}
            onChange={(d) =>
              setStartDate(d && d.isValid() ? d.format(DATE_FORMAT) : null)
            }
            disabled={loading}
            open={pickerOpen}
            onOpen={() => setPickerOpen(true)}
            onClose={() => setPickerOpen(false)}
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
                helperText: endDatePreview
                  ? `Ends ${endDatePreview}`
                  : "Pick the copy's first day",
                onClick: () => !loading && setPickerOpen(true),
                sx: {
                  "& .MuiInputBase-root": {
                    cursor: loading ? "default" : "pointer",
                  },
                },
              },
            }}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={() => startDate && onConfirm(name.trim(), startDate)}
          variant="contained"
          disabled={!canConfirm}
          startIcon={
            loading ? <CircularProgress size={16} color="inherit" /> : undefined
          }
          data-testid="duplicate-event-confirm"
        >
          Duplicate
        </Button>
      </DialogActions>
    </Dialog>
  );
}
